/**
 * iter_226 · Phase 22 — Artist "Sell This Track" wizard
 * Only from an existing RGM submission (Phase 1 scope).
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";

const fmtUsd = (n) => `$${Number(n || 0).toFixed(2)}`;

const RIGHTS_TEXT = (
  "You keep your rights. RGM Studio does not claim ownership of your music. " +
  "By publishing this single you confirm that you own or have obtained all " +
  "permissions, licenses, clearances, and authority necessary to sell and " +
  "distribute the master, composition, lyrics, beat, samples, featured " +
  "performances, and cover art. You grant RGM Studio a limited, non-exclusive " +
  "licence to host, preview, promote, sell, and securely deliver this single " +
  "while your listing is active."
);

const SellThisTrackPage = () => {
  const { submission_id } = useParams();
  const nav = useNavigate();
  const [listing, setListing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [explicit, setExplicit] = useState(false);
  const [rightsAccepted, setRightsAccepted] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        // Try to create the draft — the backend blocks duplicates.
        const r = await api.post(`/singles/list-from-track/${submission_id}`);
        setListing(r.data);
        setPrice(String(r.data.price_usd));
        setDescription(r.data.description || "");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not start listing");
        nav(-1);
      }
    })();
  }, [submission_id, nav]);

  if (!listing) return <div className="p-8 text-xs text-gray-500">Preparing your listing…</div>;

  const saveDraft = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/singles/${listing.listing_id}`, {
        price_usd: parseFloat(price),
        description,
        explicit,
      });
      setListing(r.data);
      toast.success("Draft saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      await api.post(`/singles/${listing.listing_id}/confirm-rights`, { accepted: true });
      const r = await api.post(`/singles/${listing.listing_id}/publish`);
      toast.success(r.data.status === "pending_review" ? "Submitted for review" : "Published!");
      nav("/library/singles");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally { setBusy(false); }
  };

  const priceNum = parseFloat(price || 0);
  const feePct = listing.fee_pct_preview ?? 20;
  const platformFee = Math.round(priceNum * feePct) / 100;
  const estProcessing = Math.round((priceNum * 0.029 + 0.30) * 100) / 100;
  const estNet = Math.max(0, Math.round((priceNum - platformFee - estProcessing) * 100) / 100);

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-5" data-testid="sell-this-track">
      <div>
        <p className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">Sell This Track</p>
        <h1 className="text-2xl font-black text-white mt-1">{listing.title}</h1>
        <p className="text-xs text-gray-500">Selling a personal-use download to your supporters.</p>
      </div>

      {step === 1 && (
        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3" data-testid="sell-step-details">
          <div>
            <label className="text-[10px] uppercase text-gray-500 font-black">Price (USD)</label>
            <Input type="number" step="0.01" min="0.99" max="9.99"
                   value={price} onChange={(e) => setPrice(e.target.value)}
                   data-testid="sell-price-input"
                   className="mt-1 bg-black/40 border-white/10" />
            <p className="text-[10px] text-gray-500 mt-1">$0.99 – $9.99. Recommended $1.99+.</p>
          </div>
          <div>
            <label className="text-[10px] uppercase text-gray-500 font-black">Description</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                      data-testid="sell-description-input"
                      className="mt-1 bg-black/40 border-white/10 h-24" />
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-300">
            <input type="checkbox" checked={explicit} onChange={(e) => setExplicit(e.target.checked)}
                   data-testid="sell-explicit-checkbox" />
            Marked explicit
          </label>
          <div className="rounded-lg border border-emerald-400/30 bg-emerald-500/5 p-3 text-[11px]" data-testid="sell-earnings-preview">
            <p className="text-emerald-200 font-black uppercase tracking-wide text-[10px] mb-1">Earnings preview</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-white">
              <span className="text-gray-400">Customer price</span><span className="text-right">{fmtUsd(priceNum)}</span>
              <span className="text-gray-400">RGM platform fee ({feePct}%)</span><span className="text-right">− {fmtUsd(platformFee)}</span>
              <span className="text-gray-400">Est. processing fee</span><span className="text-right">− {fmtUsd(estProcessing)}</span>
              <span className="text-emerald-200 font-black">Your net (est.)</span><span className="text-right text-emerald-200 font-black">{fmtUsd(estNet)}</span>
            </div>
            <p className="text-[9px] text-gray-500 mt-2">Processing fee is an estimate. The real amount is recorded after Stripe settles.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={saveDraft} disabled={busy} data-testid="sell-save-draft-btn" className="bg-white/10 hover:bg-white/20">Save draft</Button>
            <Button onClick={() => setStep(2)} data-testid="sell-next-btn" className="bg-cyan-500 hover:bg-cyan-400 text-black font-black">Next: Confirm rights →</Button>
          </div>
        </section>
      )}

      {step === 2 && (
        <section className="rounded-xl border border-amber-400/30 bg-amber-500/5 p-4 space-y-3" data-testid="sell-step-rights">
          <p className="text-amber-200 font-black uppercase tracking-wide text-[10px]">Rights confirmation</p>
          <p className="text-xs text-gray-200 leading-relaxed">{RIGHTS_TEXT}</p>
          <label className="flex items-start gap-2 text-xs text-white">
            <input type="checkbox" checked={rightsAccepted} onChange={(e) => setRightsAccepted(e.target.checked)}
                   data-testid="sell-rights-checkbox" />
            <span>I have read and accept the terms above.</span>
          </label>
          <div className="flex gap-2">
            <Button onClick={() => setStep(1)} className="bg-white/10 hover:bg-white/20">← Back</Button>
            <Button
              onClick={publish}
              disabled={busy || !rightsAccepted}
              data-testid="sell-publish-btn"
              className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-black font-black"
            >
              {busy ? "Publishing…" : "Publish listing"}
            </Button>
          </div>
        </section>
      )}
    </div>
  );
};

export default SellThisTrackPage;
